import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  CameraIcon,
  UserGroupIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'

interface ContentItem {
  id: string
  type: 'shoot' | 'client' | 'invoice'
  title: string
  description: string
  owner: {
    name: string
    email: string
  }
  createdAt: string
  updatedAt: string
  status: string
  metadata: any
}

const AdminContentManagementPage = () => {
  const navigate = useNavigate()
  const [contentItems, setContentItems] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [contentFilter, setContentFilter] = useState<'all' | 'shoot' | 'client' | 'invoice'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all')
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null)
  const [showItemModal, setShowItemModal] = useState(false)

  useEffect(() => {
    loadContent()
  }, [contentFilter, statusFilter])

  const loadContent = async () => {
    setLoading(true)
    try {
      // Simulate API call - in real implementation, fetch from database
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Mock data
      const mockContent: ContentItem[] = [
        {
          id: '1',
          type: 'shoot',
          title: 'Wedding Photography - Smith & Johnson',
          description: 'Outdoor wedding ceremony and reception',
          owner: { name: 'John Photographer', email: 'john@example.com' },
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-16T14:30:00Z',
          status: 'completed',
          metadata: { location: 'Central Park', duration: '8 hours', photos: 250 }
        },
        {
          id: '2',
          type: 'client',
          title: 'ABC Corporation',
          description: 'Corporate headshots and event photography',
          owner: { name: 'Sarah Studio', email: 'sarah@example.com' },
          createdAt: '2024-01-10T09:00:00Z',
          updatedAt: '2024-01-20T16:00:00Z',
          status: 'active',
          metadata: { employees: 50, projects: 3, totalValue: 15000 }
        },
        {
          id: '3',
          type: 'invoice',
          title: 'Invoice #INV-2024-001',
          description: 'Wedding photography services',
          owner: { name: 'John Photographer', email: 'john@example.com' },
          createdAt: '2024-01-16T15:00:00Z',
          updatedAt: '2024-01-17T10:00:00Z',
          status: 'paid',
          metadata: { amount: 2500, dueDate: '2024-02-15', client: 'Smith & Johnson' }
        }
      ]
      
      setContentItems(mockContent)
    } catch (error) {
      console.error('Error loading content:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      try {
        // In real implementation, delete from database
        setContentItems(prev => prev.filter(item => item.id !== itemId))
      } catch (error) {
        console.error('Error deleting item:', error)
      }
    }
  }

  const filteredContent = contentItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.owner.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = contentFilter === 'all' || item.type === contentFilter
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter
    
    return matchesSearch && matchesType && matchesStatus
  })

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'shoot': return CameraIcon
      case 'client': return UserGroupIcon
      case 'invoice': return DocumentTextIcon
      default: return DocumentTextIcon
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'shoot': return 'text-blue-400 bg-blue-500/20'
      case 'client': return 'text-green-400 bg-green-500/20'
      case 'invoice': return 'text-purple-400 bg-purple-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-500/20'
      case 'completed': return 'text-blue-400 bg-blue-500/20'
      case 'paid': return 'text-emerald-400 bg-emerald-500/20'
      case 'cancelled': return 'text-red-400 bg-red-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const contentStats = {
    total: contentItems.length,
    shoots: contentItems.filter(item => item.type === 'shoot').length,
    clients: contentItems.filter(item => item.type === 'client').length,
    invoices: contentItems.filter(item => item.type === 'invoice').length
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Content Management" subtitle="Manage shoots, clients, and content across the platform">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Admin Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DocumentTextIcon className="w-8 h-8 text-blue-400" />
              <span className="text-2xl font-bold text-white">{contentStats.total}</span>
            </div>
            <div className="text-gray-400 text-sm">Total Items</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <CameraIcon className="w-8 h-8 text-green-400" />
              <span className="text-2xl font-bold text-white">{contentStats.shoots}</span>
            </div>
            <div className="text-gray-400 text-sm">Shoots</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <UserGroupIcon className="w-8 h-8 text-purple-400" />
              <span className="text-2xl font-bold text-white">{contentStats.clients}</span>
            </div>
            <div className="text-gray-400 text-sm">Clients</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <CurrencyDollarIcon className="w-8 h-8 text-emerald-400" />
              <span className="text-2xl font-bold text-white">{contentStats.invoices}</span>
            </div>
            <div className="text-gray-400 text-sm">Invoices</div>
          </div>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-mockup rounded-2xl p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1 lg:w-80">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <select
                value={contentFilter}
                onChange={(e) => setContentFilter(e.target.value as any)}
                className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              >
                <option value="all" className="bg-gray-800">All Types</option>
                <option value="shoot" className="bg-gray-800">Shoots</option>
                <option value="client" className="bg-gray-800">Clients</option>
                <option value="invoice" className="bg-gray-800">Invoices</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              >
                <option value="all" className="bg-gray-800">All Status</option>
                <option value="active" className="bg-gray-800">Active</option>
                <option value="completed" className="bg-gray-800">Completed</option>
                <option value="cancelled" className="bg-gray-800">Cancelled</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Content Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-2xl overflow-hidden"
        >
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Loading content...</p>
            </div>
          ) : filteredContent.length === 0 ? (
            <div className="p-8 text-center">
              <DocumentTextIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No content found matching your criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Content</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Owner</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Updated</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredContent.map((item, index) => {
                    const TypeIcon = getTypeIcon(item.type)
                    return (
                                             <motion.tr
                         key={item.id}
                         initial={{ opacity: 0, y: 20 }}
                         animate={{ opacity: 1, y: 0 }}
                         transition={{ delay: index * 0.05 }}
                         className="hover:bg-white/5 transition-colors"
                       >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                              <TypeIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="ml-4">
                              <div className="text-white font-medium">{item.title}</div>
                              <div className="text-gray-400 text-sm">{item.description}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                            {item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white font-medium">{item.owner.name}</div>
                          <div className="text-gray-400 text-sm">{item.owner.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-sm">
                          {new Date(item.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedItem(item)
                                setShowItemModal(true)
                              }}
                              className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                // Navigate to edit page based on type
                                if (item.type === 'shoot') navigate(`/shoots/${item.id}/edit`)
                                else if (item.type === 'client') navigate(`/clients/${item.id}/edit`)
                                else if (item.type === 'invoice') navigate(`/invoices/${item.id}/edit`)
                              }}
                              className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Bulk Actions</h3>
            <div className="space-y-3">
              <button className="w-full px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all">
                Export All Data
              </button>
              <button className="w-full px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-all">
                Backup Content
              </button>
              <button className="w-full px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-all">
                Generate Report
              </button>
            </div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Content Health</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Items</span>
                <span className="text-green-400 font-medium">
                  {contentItems.filter(item => item.status === 'active').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Completed</span>
                <span className="text-blue-400 font-medium">
                  {contentItems.filter(item => item.status === 'completed').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Issues</span>
                <span className="text-red-400 font-medium">0</span>
              </div>
            </div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-400 text-sm">New shoot created</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-gray-400 text-sm">Invoice updated</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span className="text-gray-400 text-sm">Client added</span>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Item Detail Modal */}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">{selectedItem.title}</h2>
              <button
                onClick={() => setShowItemModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm">Type</label>
                <div className="text-white font-medium">{selectedItem.type}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Description</label>
                <div className="text-white">{selectedItem.description}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Owner</label>
                <div className="text-white">{selectedItem.owner.name} ({selectedItem.owner.email})</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Status</label>
                <div className="text-white">{selectedItem.status}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Created</label>
                <div className="text-white">{new Date(selectedItem.createdAt).toLocaleString()}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Last Updated</label>
                <div className="text-white">{new Date(selectedItem.updatedAt).toLocaleString()}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Metadata</label>
                <pre className="text-white bg-black/30 p-3 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(selectedItem.metadata, null, 2)}
                </pre>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowItemModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all"
              >
                Close
              </button>
              <button
                onClick={() => {
                  // Navigate to edit page
                  setShowItemModal(false)
                }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all"
              >
                Edit Item
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default AdminContentManagementPage 